import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Skeleton,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider
} from '@mui/material';
import { ExpandMore, LocalHospital, Category } from '@mui/icons-material';
import { formatPrice } from '../services/bookingService';

const ServiceGroupSelection = ({ services, selectedService, onServiceSelect, loading = false }) => {
  const [expandedGroups, setExpandedGroups] = useState({});

  if (loading) {
    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Select Service Category
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[1, 2, 3].map((item) => (
            <Card variant="outlined" key={item} sx={{ width: '100%', borderRadius: 1 }}>
              <CardContent sx={{ py: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Skeleton variant="text" width={200} height={32} />
                  <Skeleton variant="circular" width={24} height={24} />
                </Box>
                <Skeleton variant="text" width={300} height={20} />
                <Skeleton variant="text" width={100} height={20} />
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    );
  }

  if (!services || services.length === 0) {
    return (
      <Alert severity="info">
        No services available for this clinic. Please contact the clinic directly.
      </Alert>
    );
  }

  // Group services by category
  const groupedServices = services.reduce((groups, service) => {
    const categoryName = service.category?.name || 'Other Services';
    const categorySlug = service.category?.slug || 'other';
    const categoryDescription = service.category?.description || 'Additional services';
    
    if (!groups[categorySlug]) {
      groups[categorySlug] = {
        name: categoryName,
        slug: categorySlug,
        description: categoryDescription,
        services: []
      };
    }
    
    groups[categorySlug].services.push(service);
    return groups;
  }, {});

  // Sort groups by category sort order (if available) or alphabetically
  const sortedGroups = Object.values(groupedServices).sort((a, b) => {
    // If services have category sort_order, use that
    const aOrder = a.services[0]?.category?.sort_order || 999;
    const bOrder = b.services[0]?.category?.sort_order || 999;
    
    if (aOrder !== bOrder) {
      return aOrder - bOrder;
    }
    
    // Fallback to alphabetical
    return a.name.localeCompare(b.name);
  });

  const handleGroupToggle = (groupSlug) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupSlug]: !prev[groupSlug]
    }));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Category sx={{ mr: 1, color: 'primary.main' }} />
        <Typography variant="h6">
          Select Service Category
        </Typography>
      </Box>
      
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose from our organized service categories below. Click on a category to view available services.
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {sortedGroups.map((group) => (
          <Accordion
            key={group.slug}
            expanded={expandedGroups[group.slug] || false}
            onChange={() => handleGroupToggle(group.slug)}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: 1,
              '&:before': {
                display: 'none',
              },
              '&.Mui-expanded': {
                margin: 0,
                '& .MuiAccordionSummary-root': {
                  borderBottom: '1px solid #e0e0e0',
                }
              }
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                backgroundColor: '#f8f9fa',
                borderRadius: '4px 4px 0 0',
                '&.Mui-expanded': {
                  borderRadius: '4px 4px 0 0',
                }
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', mr: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LocalHospital sx={{ mr: 1, color: 'primary.main' }} />
                  <Box>
                    <Typography variant="subtitle1" fontWeight="medium">
                      {group.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {group.description}
                    </Typography>
                  </Box>
                </Box>
                <Chip 
                  label={`${group.services.length} service${group.services.length !== 1 ? 's' : ''}`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </AccordionSummary>
            
            <AccordionDetails sx={{ p: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {group.services.map((service, index) => (
                  <React.Fragment key={service.id}>
                    <Card 
                      variant="outlined"
                      sx={{
                        width: '100%',
                        border: 'none',
                        borderRadius: 0,
                        boxShadow: 'none',
                        transition: 'background-color 0.2s',
                        ...(selectedService && selectedService.id === service.id ? {
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '& .MuiTypography-root': {
                            color: 'white'
                          }
                        } : {
                          '&:hover': {
                            backgroundColor: '#f5f5f5'
                          }
                        })
                      }}
                    >
                      <CardActionArea 
                        onClick={() => onServiceSelect(service)}
                        sx={{ width: '100%' }}
                      >
                        <CardContent sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 2 }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight="medium">
                              {service.name}
                            </Typography>
                            <Typography 
                              variant="body2" 
                              color={selectedService && selectedService.id === service.id ? 'inherit' : 'text.secondary'}
                            >
                              Duration: {service.duration_in_minutes} minutes
                            </Typography>
                          </Box>
                          <Typography 
                            variant="subtitle1" 
                            color={selectedService && selectedService.id === service.id ? 'inherit' : 'primary'}
                            fontWeight="bold"
                          >
                            {formatPrice(service.price)}
                          </Typography>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                    {index < group.services.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    </Box>
  );
};

export default ServiceGroupSelection;